import { BrandWordmark } from '../components/Brand.js';
/**
 * Signed-in chrome (UX/UI Source v0.3 layout, Blueprint §3 menu).
 *
 * Desktop: persistent workspace rail with Thai sections (app/nav.ts); tablet: horizontal navigation.
 * Phone (≤ 720 px): compact row 1, and the main menu becomes a bottom tab bar — หน้าแรก plus three
 * (the person's pins first, else งานนำเสนอ, แชท, กิจกรรม) — and "เมนู" for the rest. Settings live in the account menu.
 * "ปรับแต่งเมนู" (pins and their order) is a dialog of its own chunk; the team's hidden entries are set in ตั้งค่า.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import ALargeSmall from 'lucide-react/dist/esm/icons/a-large-small.mjs'; import Building2 from 'lucide-react/dist/esm/icons/building-complex.mjs'; import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs'; import CircleHelp from 'lucide-react/dist/esm/icons/circle-question-mark.mjs'; import House from 'lucide-react/dist/esm/icons/house.mjs'; import LayoutTemplate from 'lucide-react/dist/esm/icons/layout-template.mjs'; import LogOut from 'lucide-react/dist/esm/icons/log-out.mjs'; import Menu from 'lucide-react/dist/esm/icons/menu.mjs'; import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.mjs'; import Pin from 'lucide-react/dist/esm/icons/pin.mjs'; import Search from 'lucide-react/dist/esm/icons/search.mjs'; import Settings from 'lucide-react/dist/esm/icons/settings.mjs'; import Star from 'lucide-react/dist/esm/icons/star.mjs';
import type { WorkspaceSummary } from '../api/types.js';
import { api, describeError } from '../api/client.js';
import { Dialog } from '../components/Dialog.js';
import { Popover } from '../components/Popover.js';
import { PresenceAvatars } from '../components/Presence.js';
import { ActivityBell } from '../components/ActivityBell.js';
import { useToast } from '../components/Toast.js';
import { href, navigate } from '../lib/router.js';
import { useRememberedFolder, withFolder } from './folders.js';
import { HelpButton, HelpNavLink } from '../help/HelpHost.js';
import { ThemeSwitch } from './ThemeSwitch.js';
import { useApprovalSummary } from './approvals.js';
import { useTaskBadge } from './tasks.js';
import { useSession } from './session.js';
import '../styles/home.css';
import { useChat } from '../chat/store.js';
import { setDockOpen } from '../chat/ChatDock.js';
import { openQuickSwitcher, preloadQuickSwitcher } from '../chat/ChatHost.js';
import { lazyDialog } from '../components/LazyFallback.js';
import { whenIdle } from '../lib/lazy.js';
import { menuModel, modKey, setMenuCustomizer, type NavItem } from './nav.js';

// "ปรับแต่งเมนู": downloaded when it is first opened (or warmed when the pointer reaches its button).
const NavCustomizeDialog = lazyDialog(() => import('./NavCustomizeDialog.js'), 'NavCustomizeDialog');
// "การแสดงผล" (the header popover, the phone menu sheet): a chunk of its own, warmed once the page has settled.
const DisplaySettings = lazyDialog(() => import('./DisplaySettings.js'), 'DisplaySettings');

// Whether the phone "เมนู" sheet is open survives a swap of the shell. While a page's chunk is on
// its way, App.tsx shows RouteFallback — a second AppShell instance — and the sheet the person just
// opened would close the moment the real page replaces it, under their finger (PH-02 nightly:
// chat-mobile's sheet link detached mid-click). The flag lets the replacing shell reopen it.
let phoneMenuOpen = false;

const ROLE_LABEL: Record<string, string> = { OWNER: 'เจ้าของ', ADMIN: 'ผู้ดูแล', DESIGNER: 'นักออกแบบ', EDITOR: 'ผู้แก้ไข', REVIEWER: 'ผู้ตรวจ', VIEWER: 'ผู้ชม' };
export const roleLabel = (r: string) => ROLE_LABEL[r] ?? r;

/**
 * The switcher's groups when the person has a home workspace: "แผนกของฉัน" (or "หน้าหลัก" when the
 * home is not a department workspace), then "พื้นที่ส่วนตัว", then the others. No home = one flat list.
 */
export function workspaceGroups(workspaces: WorkspaceSummary[], homeId: string | null): { label: string | null; items: WorkspaceSummary[] }[] {
  const home = homeId ? workspaces.find((w) => w.id === homeId) : undefined;
  if (!home) return [{ label: null, items: workspaces }];
  const personal = workspaces.filter((w) => w.id !== home.id && w.kind === 'personal' && w.role === 'OWNER');
  const others = workspaces.filter((w) => w.id !== home.id && !personal.includes(w));
  return [
    { label: home.department ? 'แผนกของฉัน' : 'หน้าหลัก', items: [home] },
    { label: 'พื้นที่ส่วนตัว', items: personal },
    { label: 'เวิร์กสเปซอื่น', items: others },
  ].filter((g) => g.items.length > 0);
}

export const isCurrent = (current: string, path: string) => current === path || (path !== '/' && current.startsWith(path + '/'));

const initials = (name: string) => {
  const t = name.trim();
  if (!t) return '?';
  const parts = t.split(/[\s._-]+/).filter(Boolean);
  const first = [...(parts[0] ?? t)][0] ?? '?';
  const second = parts.length > 1 ? [...parts[1]!][0] ?? '' : '';
  return (first + second).toUpperCase();
};

export function Wordmark() { return <BrandWordmark />; }

export function AppShell({ current, children, wide = false }: { current: string; children: ReactNode; wide?: boolean }) {
  const session = useSession();
  const chat = useChat();
  const unreadChats = chat.unreadChats;
  const unreadActivity = chat.activityUnread;
  const notify = useToast();
  // ระบบอนุมัติงาน (ADR 0027): what waits for this person's decision.
  const pending = useApprovalSummary()?.waitingForMe ?? 0;
  const overdueTasks = useTaskBadge();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [moreOpen, setMoreOpen] = useState(phoneMenuOpen);
  // Every open/close goes through these, so `phoneMenuOpen` stays the truth across a shell swap.
  const openMore = () => {
    DisplaySettings.preload(); // the sheet's "การแสดงผล" section must arrive with the sheet, not after it
    phoneMenuOpen = true;
    setMoreOpen(true);
  };
  const closeMore = () => {
    phoneMenuOpen = false;
    setMoreOpen(false);
  };
  const [customizing, setCustomizing] = useState(false);
  // The Ctrl+K command "ปรับแต่งเมนู" opens the same dialog while this shell is on screen.
  useEffect(() => {
    setMenuCustomizer(() => setCustomizing(true));
    return () => setMenuCustomizer(null);
  }, []);
  useEffect(() => whenIdle(DisplaySettings.preload, 8000), []);
  const user = session.user;
  const headerRef = useRef<HTMLElement>(null);

  // Publish the real header height (it changes with text size and width) for sticky offsets.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const ro = new ResizeObserver(() => root.style.setProperty('--xc-appbar-h', `${Math.round(el.getBoundingClientRect().height)}px`));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const createWorkspace = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const ws = await api.createWorkspace(name.trim());
      session.addWorkspace(ws);
      session.setWorkspace(ws.id);
      setCreating(false);
      setName('');
      notify(`สร้างเวิร์กสเปซ “${ws.name}” แล้ว`, 'success');
    } catch (err) {
      notify(describeError(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  // The folder the person is in travels with them: links to the pages that show the tree keep it (ADR 0043).
  const folderHere = useRememberedFolder();
  const countOf = (path: string) => (path === '/reviews' ? pending : path === '/tasks' ? overdueTasks : path === '/chat' ? unreadChats : path === '/activity' ? unreadActivity : 0);
  const badge = (path: string) => {
    const n = countOf(path);
    if (!n) return null;
    const what = path === '/reviews' ? 'รายการรอคุณอนุมัติ' : path === '/tasks' ? 'งานเกินกำหนด' : path === '/chat' ? 'แชทที่ยังไม่อ่าน' : 'กิจกรรมที่ยังไม่อ่าน';
    return (
      <span className="xc-count-badge" aria-label={`${n} ${what}`}>
        {n > 99 ? '99+' : n}
      </span>
    );
  };

  // Sections, pins and the phone bar for this person in this workspace (app/nav.ts) — from the session, no request.
  const menu = menuModel(session.workspace);
  const navLink = (n: NavItem, extra?: () => void) => {
    const on = isCurrent(current, n.path);
    const Icon = n.icon;
    return (
      <a key={n.path} href={href(withFolder(n.path, folderHere))} className={on ? 'is-on' : ''} aria-current={on ? 'page' : undefined} onClick={extra}>
        <Icon aria-hidden="true" size={extra ? 20 : 18} />
        <span>{n.label}</span>
        {badge(n.path)}
      </a>
    );
  };
  const groups = workspaceGroups(session.workspaces, session.homeWorkspaceId);
  const isHome = !!session.workspace && session.workspace.id === session.homeWorkspaceId;
  const makeHome = async (id: string | null) => {
    try {
      await session.setHome(id);
      notify(id ? `ตั้ง “${session.workspaces.find((w) => w.id === id)?.name ?? ''}” เป็นหน้าหลักแล้ว — เปิดแอปครั้งถัดไปจะเริ่มที่นี่` : 'หน้าหลักกลับไปตามแผนกของคุณแล้ว', 'success');
    } catch (err) {
      notify(describeError(err), 'error');
    }
  };
  const optionLabel = (w: WorkspaceSummary) => `${w.name} · ${w.kind === 'personal' ? 'ส่วนตัว' : w.department ? 'แผนก' : 'ทีม'}${session.selfHosted ? '' : ` · ${w.plan}`}`;

  const moreCurrent = !menu.tabs.some((t) => isCurrent(current, t.path) && (t.path !== '/' || current === '/')) || current === '/settings';
  // The "เมนู" button carries a dot when something in its sheet is waiting (approvals, unread chats …).
  const moreWaiting = menu.more.reduce((n, item) => n + countOf(item.path), 0);
  const key = modKey();

  return (
    <div className="xc-app" data-section={current.split('/')[1] || 'home'}>
      <a
        className="xc-skip"
        href="#xc-main"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('xc-main')?.focus();
        }}
      >
        ข้ามไปยังเนื้อหา
      </a>
      <header className="xc-appbar" ref={headerRef}>
        <div className="xc-appbar-row">
          <a href={href('/')} className="xc-logo" aria-label="XFORMA หน้าแรก">
            <Wordmark />
          </a>
          {session.workspaces.length > 0 && (
            <label className="xc-ws-switch">
              <Building2 aria-hidden="true" size={16} />
              <span className="xc-sr-only">เวิร์กสเปซ</span>
              <select
                value={session.workspace?.id ?? ''}
                onChange={(e) => {
                  if (e.target.value === '__new') setCreating(true);
                  else session.setWorkspace(e.target.value);
                }}
              >
                {groups.map((g) =>
                  g.label ? (
                    <optgroup key={g.label} label={g.label}>
                      {g.items.map((w) => <option key={w.id} value={w.id}>{optionLabel(w)}</option>)}
                    </optgroup>
                  ) : (
                    g.items.map((w) => <option key={w.id} value={w.id}>{optionLabel(w)}</option>)
                  ),
                )}
                <option value="__new">＋ สร้างเวิร์กสเปซทีม…</option>
              </select>
              <ChevronDown aria-hidden="true" size={14} className="xc-ws-caret" />
            </label>
          )}
          {session.workspace && session.workspaces.length > 1 && (isHome ? (
            <span className="xc-ws-home is-home" role="img" aria-label="เวิร์กสเปซนี้เป็นหน้าหลักของคุณ" title="หน้าหลักของคุณ — เปิดแอปแล้วเริ่มที่นี่">
              <Star aria-hidden="true" size={17} fill="currentColor" />
            </span>
          ) : (
            <button type="button" className="xc-ws-home" aria-label={`ตั้ง ${session.workspace.name} เป็นหน้าหลัก`} title="ตั้งเป็นหน้าหลัก — เปิดแอปแล้วเริ่มที่เวิร์กสเปซนี้" onClick={() => void makeHome(session.workspace!.id)}>
              <Star aria-hidden="true" size={17} />
            </button>
          ))}
          <div className="xc-appbar-right">
            {session.workspace && (
              <button type="button" className="xc-icon-btn xc-find-btn" onClick={() => openQuickSwitcher()} onPointerEnter={preloadQuickSwitcher} onFocus={preloadQuickSwitcher} aria-label={`ค้นหาและคำสั่ง (${key}+K)`} title={`ค้นหางาน ไฟล์ หน้า และคำสั่ง (${key}+K)`} aria-haspopup="dialog" aria-keyshortcuts="Control+K Meta+K">
                <Search aria-hidden="true" size={20} /><span className="xf-search-hint">ค้นหางาน ไฟล์ หรือคำสั่ง…</span><kbd className="xf-search-key">{key} K</kbd>
              </button>
            )}
            {session.workspace && ['OWNER', 'ADMIN'].includes(session.workspace.role) && <a className="xc-btn xf-invite" href={href('/settings')}>เชิญทีมงาน</a>}
            <PresenceAvatars />
            <button type="button" className="xc-icon-btn xc-dock-toggle" onClick={() => setDockOpen()} aria-label={`แถบแชท (Ctrl+Shift+M)${unreadChats ? ` — ยังไม่อ่าน ${unreadChats} แชท` : ''}`} title="แชท (Ctrl+Shift+M)">
              <MessageCircle aria-hidden="true" size={20} />
              {unreadChats > 0 && <span className="xc-dot" aria-hidden="true" />}
            </button>
            <ActivityBell />
            <HelpButton current={current} />
            <ThemeSwitch />
            {session.workspace && <span className="xc-account-role">{roleLabel(session.workspace.role)}</span>}
            <Popover label="การแสดงผล" trigger={<ALargeSmall aria-hidden="true" size={20} />}>
              {() => (
                <div className="xc-menu-panel">
                  <p className="xc-menu-title">การแสดงผล</p>
                  <DisplaySettings compact />
                </div>
              )}
            </Popover>
            <Popover
              label="บัญชีผู้ใช้"
              triggerClassName="xc-avatar-btn"
              trigger={
                <span className="xc-avatar" aria-hidden="true">
                  {initials(user?.displayName ?? user?.email ?? '')}
                </span>
              }
            >
              {(close) => (
                <div className="xc-menu-panel">
                  <div className="xc-menu-user">
                    <strong>{user?.displayName}</strong>
                    <span>{user?.email}</span>
                    {session.workspace && (
                      <span>
                        {session.workspace.name} · {roleLabel(session.workspace.role)}
                      </span>
                    )}
                  </div>
                  {session.workspace && session.workspaces.length > 1 && !isHome && (
                    <button type="button" className="xc-menu-item" onClick={() => { close(); void makeHome(session.workspace!.id); }}>
                      <Star aria-hidden="true" size={18} /> ตั้งเป็นหน้าหลัก
                    </button>
                  )}
                  {session.homeWorkspaceSource === 'user' && (
                    <button type="button" className="xc-menu-item" onClick={() => { close(); void makeHome(null); }}>
                      <House aria-hidden="true" size={18} /> ใช้แผนกของฉันเป็นหน้าหลัก
                    </button>
                  )}
                  <a
                    className="xc-menu-item"
                    href={href('/settings')}
                    onClick={() => close()}
                    aria-current={current === '/settings' ? 'page' : undefined}
                  >
                    <Settings aria-hidden="true" size={18} /> ตั้งค่าและสมาชิก
                  </a>
                  <button
                    type="button"
                    className="xc-menu-item"
                    onClick={() => {
                      close();
                      void session.logout().then(() => navigate('/'));
                    }}
                  >
                    <LogOut aria-hidden="true" size={18} /> ออกจากระบบ
                  </button>
                </div>
              )}
            </Popover>
          </div>
        </div>
        <nav className="xc-nav" aria-label="เมนูหลัก">
          <a className="xc-nav-create" href={href('/templates')}><LayoutTemplate size={19} aria-hidden="true" /><span>สร้างงานออกแบบ</span><span aria-hidden="true">＋</span></a>
          {menu.pins.length > 0 && (
            <>
              <span className="xc-nav-caption xc-nav-caption-pins">ปักหมุด</span>
              {menu.pins.map((n) => navLink(n))}
            </>
          )}
          {menu.sections.map((sec) => (
            <Fragment key={sec.id}>
              <span className="xc-nav-caption">{sec.label}</span>
              {sec.items.map((n) => navLink(n))}
            </Fragment>
          ))}
          <button type="button" className="xc-nav-customize" onClick={() => setCustomizing(true)} onPointerEnter={NavCustomizeDialog.preload} onFocus={NavCustomizeDialog.preload}>
            <Pin aria-hidden="true" size={16} />
            <span>ปรับแต่งเมนู</span>
          </button>
          <div className="xc-nav-footer">
            <HelpNavLink current={current} />
            <a href={href('/settings')} className={current === '/settings' ? 'is-on' : ''} aria-current={current === '/settings' ? 'page' : undefined}>
              <Settings aria-hidden="true" size={18} />
              <span>ตั้งค่าและสมาชิก</span>
            </a>
            <div className="xc-workspace-card"><span className="xc-workspace-dot" /><strong>{session.user?.displayName || session.user?.email || 'XFORMA'}</strong><small>{session.workspace ? roleLabel(session.workspace.role) : 'บัญชีของคุณ'}</small></div>
          </div>
        </nav>
      </header>
      {session.expired && (
        <div className="xc-banner xc-banner-warn" role="alert">
          เซสชันหมดอายุ — งานในหน้านี้ยังอยู่ เข้าสู่ระบบอีกครั้งได้โดยไม่ต้องออกจากหน้า{' '}
          <button type="button" className="xc-btn xc-btn-sm xc-btn-primary" onClick={() => session.openSignIn()}>
            เข้าสู่ระบบอีกครั้ง
          </button>
        </div>
      )}
      <main id="xc-main" className={`xc-main${wide ? ' is-wide' : ''}`} tabIndex={-1}>
        {children}
      </main>

      {/* Phone: bottom tab bar (the header menu is hidden at this width). */}
      <nav className="xc-bottomnav" aria-label="เมนูหลัก (มือถือ)">
        {menu.tabs.map((t) => {
          const on = t.path === '/' ? current === '/' : isCurrent(current, t.path);
          const Icon = t.icon;
          const n = countOf(t.path);
          return (
            <a key={t.path} href={href(withFolder(t.path, folderHere))} className={on ? 'is-on' : ''} aria-current={on ? 'page' : undefined} aria-label={n ? `${t.tab ?? t.label} (${n} ยังไม่อ่าน)` : undefined}>
              <Icon aria-hidden="true" size={22} />
              <span>{t.tab ?? t.label}</span>
              {n > 0 && <span className="xc-tab-badge" aria-hidden="true">{n > 99 ? '99+' : n}</span>}
            </a>
          );
        })}
        <button type="button" className={moreCurrent ? 'is-on' : ''} aria-expanded={moreOpen} onPointerEnter={DisplaySettings.preload} onClick={openMore}>
          <Menu aria-hidden="true" size={22} />
          <span>เมนู</span>
          {moreWaiting > 0 && <span className="xc-dot" aria-label={pending > 0 ? `${pending} รายการรอคุณอนุมัติ` : `${moreWaiting} รายการรอคุณ`} />}
        </button>
      </nav>

      {moreOpen && (
        <Dialog title="เมนู" onClose={closeMore} size="sm">
          <nav className="xc-sheet-nav" aria-label="เมนูเพิ่มเติม">
            {/* The header's search button does not fit the narrowest phones (≤ 380 px): the same palette from here. */}
            <button type="button" className="xc-sheet-find" onClick={() => { closeMore(); openQuickSwitcher(); }}>
              <Search aria-hidden="true" size={20} />
              <span>ค้นหาและคำสั่ง</span>
            </button>
            {menu.more.map((n) => navLink(n, closeMore))}
            {[{ path: '/help', label: 'ศูนย์ช่วยเหลือ', icon: CircleHelp }, { path: '/settings', label: 'ตั้งค่าและสมาชิก', icon: Settings }].map((n) => {
              const Icon = n.icon;
              const on = isCurrent(current, n.path);
              return (
                <a key={n.path} href={href(n.path)} className={on ? 'is-on' : ''} aria-current={on ? 'page' : undefined} onClick={closeMore}>
                  <Icon aria-hidden="true" size={20} />
                  <span>{n.label}</span>
                </a>
              );
            })}
            <button type="button" className="xc-sheet-find" onClick={() => { closeMore(); setCustomizing(true); }}>
              <Pin aria-hidden="true" size={20} />
              <span>ปรับแต่งเมนู</span>
            </button>
          </nav>
          <div className="xc-sheet-section">
            <p className="xc-menu-title">การแสดงผล</p>
            <DisplaySettings compact />
          </div>
        </Dialog>
      )}

      {customizing && session.workspace && <NavCustomizeDialog onClose={() => setCustomizing(false)} />}

      {creating && (
        <Dialog title="สร้างเวิร์กสเปซทีม" onClose={() => setCreating(false)} size="sm">
          <form onSubmit={createWorkspace} className="xc-form">
            <label className="xc-field">
              <span className="xc-field-label">ชื่อเวิร์กสเปซ</span>
              <span className="xc-field-input">
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required data-autofocus />
              </span>
            </label>
            <button type="submit" className="xc-btn xc-btn-primary" disabled={busy}>
              สร้าง
            </button>
          </form>
        </Dialog>
      )}
    </div>
  );
}
